import ContentSaveIcon from '@mui/icons-material/Save';
import { Button as MuiButton, Stack } from '@mui/material';
import { FormEvent, useEffect, useMemo } from 'react';
import {
    RecordContextProvider,
    Toolbar,
    useCreate,
    useDelete,
    useNotify,
    useUpdate,
} from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { FormationInput, formationSummaryToInput } from '@domain/formation';
import { resourceInputPayload } from '@domain/types';
import type { FormationSummary } from '@wwf/sdk';
import { FormationInputs } from './FormationInputs';

type DrawerMode = 'create' | 'edit' | 'show';

const defaultValues: FormationInput = {
    name: '',
    description: '',
    url: null,
};

export function FormationDrawerForm({
    mode,
    record,
    loading,
    onClose,
    onSuccess,
}: {
    mode: DrawerMode;
    record?: FormationSummary;
    loading: boolean;
    onClose(): void;
    onSuccess(): void;
}) {
    const notify = useNotify();
    const [create] = useCreate();
    const [update] = useUpdate();
    const [deleteOne] = useDelete();

    const form = useForm<FormationInput>({
        defaultValues,
    });

    const formRecord = useMemo(
        () =>
            record && (mode === 'edit' || mode === 'show')
                ? formationSummaryToInput(record)
                : undefined,
        [record, mode]
    );

    useEffect(() => {
        if (formRecord) {
            form.reset(formRecord);
        } else {
            form.reset(defaultValues);
        }
    }, [formRecord, form]);

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (mode === 'show') return;
        form.handleSubmit(async (data: FormationInput) => {
            try {
                const editionData: FormationInput = {
                    name: data.name,
                    description: data.description ?? null,
                    url: data.url ?? null,
                    ...resourceInputPayload(
                        { inputKey: 'coverInput', idKey: 'coverId' },
                        data.coverInput,
                        data.coverId
                    ),
                };

                if (mode === 'create') {
                    await create(
                        'formation',
                        {
                            data: editionData,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                } else {
                    if (!record?.id) {
                        notify('Record not found', { type: 'error' });
                        return;
                    }
                    await update(
                        'formation',
                        {
                            id: record.id,
                            data: editionData,
                            previousData: record as any,
                            meta: {},
                        },
                        {
                            onSuccess: () => {
                                onSuccess();
                                onClose();
                            },
                        }
                    );
                }
            } catch (e) {
                notify(String(e), { type: 'error' });
            }
        })(event);
    };

    const handleDelete = () => {
        if (!record?.id) return;
        deleteOne(
            'formation',
            { id: record.id, previousData: record as any },
            {
                onSuccess: () => {
                    onSuccess();
                    onClose();
                },
            }
        );
    };

    const saving = form.formState.isSubmitting;
    const canSave = !loading && !saving;

    return (
        <FormProvider {...form}>
            <RecordContextProvider value={formRecord ?? undefined}>
                <form onSubmit={handleSubmit}>
                    <Stack gap={2}>
                        <FormationInputs readOnly={mode === 'show'} />
                    </Stack>
                    {mode !== 'show' && (
                        <Toolbar
                            sx={{
                                mt: 2,
                                px: 0,
                                justifyContent: 'space-between',
                            }}
                        >
                            <Stack direction="row" gap={1}>
                                <MuiButton
                                    variant="contained"
                                    type="submit"
                                    disabled={!canSave}
                                    startIcon={<ContentSaveIcon />}
                                >
                                    Save
                                </MuiButton>
                                <MuiButton
                                    variant="text"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Cancel
                                </MuiButton>
                            </Stack>
                            {mode === 'edit' && record?.id && (
                                <MuiButton
                                    variant="text"
                                    color="error"
                                    disabled={saving}
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                'Are you sure you want to delete this formation?'
                                            )
                                        ) {
                                            handleDelete();
                                        }
                                    }}
                                >
                                    Delete
                                </MuiButton>
                            )}
                        </Toolbar>
                    )}
                </form>
            </RecordContextProvider>
        </FormProvider>
    );
}
